import {
  FloorPreservationToggle,
  GenerationModeSelector,
  ModeSelector,
  RoomTypeSelector,
  WallColorPicker
} from '../index'

import { AiLabelSection, AiLabelType } from '../../../AiLabel'
import type { AiLabelColour, AiLabelState } from '../../../AiLabel'

import { BEIGE_400 } from 'constants/styling/theme'
import { FloorTemplatePicker } from '../FloorTemplatePicker/FloorTemplatePicker.component'
import { GenerateButton } from '../../../common/GenerateButton'
import { GenerationType } from 'constants/visualStaging/visualStagingTypes'
import React from 'react'
import { Stack } from '@mui/material'
import { StyleSelector } from '../../../VisualStaging/components/StyleSelector'
import { VirtualStagingState } from 'models/visualStaging/virtualStagingTypes'
import { useTranslation } from 'react-i18next'

interface VirtualStagingPanelProps {
  virtualStagingState: VirtualStagingState
  onGenerationTypeChange: (type: any) => void
  onProcessingModeChange: (mode: any) => void
  onRoomTypeChange: (type: any) => void
  onStyleChange: (style: any) => void
  onFloorToggle: () => void
  onWallColorChange?: (color: string) => void
  onFloorTemplateChange?: (template: string) => void
  aiLabelState?: AiLabelState
  onAiLabelToggle?: (enabled: boolean) => void
  onAiLabelTypeChange?: (type: AiLabelType) => void
  onAiLabelTextChange?: (text: string) => void
  onAiLabelColourChange?: (colour: AiLabelColour) => void
  onAiLabelOpacityChange?: (opacity: number) => void
  onAiLabelResetPlacement?: () => void
  isDisabled?: boolean
  onGenerate?: () => void
  isProcessingImage?: boolean
  hasMask?: boolean
  isMaskRequired?: boolean
  canGenerate?: boolean
}

/**
 * VirtualStagingPanel component for Virtual Staging.
 * Main left sidebar component that combines all Virtual Staging controls.
 * Provides a cohesive interface for configuring Virtual Staging parameters.
 * Includes generation mode, processing mode, room type, floor preservation, and AI label controls.
 * 
 * @example
 * <VirtualStagingPanel
 *   virtualStagingState={virtualStagingState}
 *   onGenerationTypeChange={changeGenerationType}
 *   onProcessingModeChange={changeProcessingMode}
 *   onRoomTypeChange={changeRoomType}
 *   onStyleChange={changeSelectedStyle}
 *   onFloorToggle={toggleFloorPreservation}
 *   isDisabled={isProcessing}
 * />
 */
export const VirtualStagingPanel: React.FC<VirtualStagingPanelProps> = ({
  virtualStagingState,
  onGenerationTypeChange,
  onProcessingModeChange,
  onRoomTypeChange,
  onStyleChange,
  onFloorToggle,
  onWallColorChange,
  onFloorTemplateChange,
  aiLabelState,
  onAiLabelToggle,
  onAiLabelTypeChange,
  onAiLabelTextChange,
  onAiLabelColourChange,
  onAiLabelOpacityChange,
  onAiLabelResetPlacement,
  isDisabled = false,
  onGenerate,
  isProcessingImage = false,
  hasMask = false,
  isMaskRequired = false,
  canGenerate,
}) => {
  const { t } = useTranslation('photo_studio_editing')

  const isDeclutterMode = virtualStagingState.generationType === GenerationType.DECLUTTER_ROOM
  const isPaintWallsMode = virtualStagingState.generationType === GenerationType.PAINT_WALLS
  const isChangeFloorMode = virtualStagingState.generationType === GenerationType.CHANGE_FLOOR

  const hasRoomType = !!virtualStagingState.roomType
  const hasStyle = !!virtualStagingState.selectedStyle
  const hasWallColor = !!virtualStagingState.wallColor
  const hasFloorTemplate = !!virtualStagingState.floorTemplate
  const roomTypeError = !hasRoomType ? t('virtual_staging.room_type.room_type_required') : undefined

  // Determine if form trigger conditions are met based on generation mode
  const getHasFormTrigger = (): boolean => {
    if (isDeclutterMode) {
      return true  // Always enabled - no selections required
    }
    if (isPaintWallsMode) {
      return hasWallColor
    }
    if (isChangeFloorMode) {
      return hasFloorTemplate
    }
    return hasRoomType && hasStyle && (!isMaskRequired || hasMask)
  }

  const hasFormTrigger = getHasFormTrigger()
  const effectiveCanGenerate = typeof canGenerate === 'boolean' ? canGenerate : hasFormTrigger

  // Determine if Generate button should be disabled
  const isGenerateDisabled = isProcessingImage || !effectiveCanGenerate || isDisabled

  // Get tooltip for disabled state
  const getGenerateButtonTooltip = () => {
    if (isDeclutterMode) {
      return undefined  // No tooltip - always enabled
    }
    if (isPaintWallsMode) {
      if (!hasWallColor) {
        return t('virtual_staging.wall_colors.color_required')
      }
    } else if (isChangeFloorMode) {
      if (!hasFloorTemplate) {
        return t('virtual_staging.floor_templates.template_required')
      }
    } else {
      if (!hasRoomType) {
        return t('virtual_staging.room_type.room_type_required')
      }
      if (!hasStyle) {
        return t('virtual_staging.style_selector.style_required')
      }
      if (isMaskRequired && !hasMask) {
        return t('disabled_generate_button')
      }
    }
    return undefined
  }

  return (
    <Stack
      sx={{
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: '2.4rem',
        paddingLeft: '2.4rem',
        paddingRight: '2.4rem',
        boxSizing: 'border-box',
      }}
    >
      <Stack
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          gap: '2.4rem',
          paddingBottom: '1.6rem',
          paddingRight: '1.5rem', // Space for scrollbar
          marginRight: '-1.5rem', // Pull scrollbar to align with right edge
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: BEIGE_400,
            borderRadius: '4px',
          },
        }}
      >
        {/* Generation Mode Selection */}
        <GenerationModeSelector
          generationType={virtualStagingState.generationType}
          onGenerationTypeChange={onGenerationTypeChange}
          isDisabled={isDisabled}
        />

        {/* Declutter Mode - Show nothing extra (just the AI label below) */}
        {/* Paint Walls Mode - Show only color picker */}
        {/* Change Floor Mode - Show only floor template picker */}
        {/* Standard Modes - Show Room Type, Style, Processing Mode, Floor Preservation */}
        {isDeclutterMode ? (
          null  // No extra controls for declutter - just the AI label below
        ) : isPaintWallsMode ? (
          <WallColorPicker
            selectedColor={virtualStagingState.wallColor}
            onColorChange={onWallColorChange || (() => { })}
            isDisabled={isDisabled}
          />
        ) : isChangeFloorMode ? (
          <FloorTemplatePicker
            selectedTemplate={virtualStagingState.floorTemplate}
            onTemplateChange={onFloorTemplateChange || (() => { })}
            isDisabled={isDisabled}
          />
        ) : (
          <>
            {/* Room Type Selection */}
            <RoomTypeSelector
              roomType={virtualStagingState.roomType}
              onRoomTypeChange={onRoomTypeChange}
              isDisabled={isDisabled}
              error={roomTypeError}
            />

            {/* Style Selection - 2 column grid under Room Type */}
            {virtualStagingState.roomType && (
              <StyleSelector
                roomType={virtualStagingState.roomType}
                selectedStyle={virtualStagingState.selectedStyle}
                onStyleChange={onStyleChange}
                isDisabled={isDisabled}
              />
            )}

            {/* Processing Mode Selection */}
            <ModeSelector
              processingMode={virtualStagingState.processingMode}
              onProcessingModeChange={onProcessingModeChange}
              isDisabled={isDisabled}
            />

            {/* Floor Preservation Toggle */}
            <FloorPreservationToggle
              keepFloorUntouched={virtualStagingState.keepFloorUntouched}
              onToggle={onFloorToggle}
              isDisabled={isDisabled}
            />
          </>
        )}

        {/* AI Label Section (EU AI Act) - optional in every Photo Studio tool */}
        {aiLabelState && (
          <AiLabelSection
            showAiLabel={aiLabelState.showAiLabel}
            onToggle={onAiLabelToggle || (() => { })}
            labelType={aiLabelState.labelType}
            onLabelTypeChange={onAiLabelTypeChange || (() => { })}
            labelText={aiLabelState.labelText}
            onLabelTextChange={onAiLabelTextChange || (() => { })}
            colour={aiLabelState.colour}
            onColourChange={onAiLabelColourChange || (() => { })}
            opacity={aiLabelState.opacity}
            onOpacityChange={onAiLabelOpacityChange || (() => { })}
            onResetPlacement={onAiLabelResetPlacement || (() => { })}
            isDisabled={isDisabled}
          />
        )}
      </Stack>

      {/* Generate Button - Fixed at bottom, always visible */}
      {onGenerate && (
        <GenerateButton
          onGenerate={onGenerate}
          disabled={isGenerateDisabled}
          disabledTooltip={isGenerateDisabled ? getGenerateButtonTooltip() : undefined}
        />
      )}
    </Stack>
  )
}
